/**
 * Thin fetch wrappers around the Test Composer HTTP surface (Module 3).
 *
 * Mirrors the envelope shape used by the questions/auth state. TanStack
 * Query hooks are deferred to Module 11.
 *
 * // CARVE-OUT (04-test-composer/02 review round 1, 2026-05-23): admin/tests path.
 * // This file lives at state/tests/ (state layer for admin/tests pages) outside
 * // the denylist's pages/tests/** but this path was established by issue
 * // 04-test-composer/01 and ratified at merge.
 * // Ratified by reviewer commit db212cd — see issue file's ## Review.
 */

export type Track = 'architecture' | 'mep' | 'structural' | 'interior';
export type Level = 'novice' | 'intermediate' | 'advanced' | 'bim_champion';
export type TestStatus = 'draft' | 'active' | 'archived';
export type ResitLockoutPolicy = 'none' | '48hours' | '1week';

export interface TestPayload {
  id: string;
  title: string;
  description: string;
  track: Track | null;
  level: Level | null;
  time_limit: number;
  pass_mark: number;
  shuffle_q: boolean;
  shuffle_a: boolean;
  show_result: boolean;
  estimated_minutes: number;
  resit_lockout: ResitLockoutPolicy;
  issue_certificate: boolean;
  status: TestStatus;
  task_artifact_url: string | null;
  task_brief_md: string | null;
  author_user_id: string | null;
  author_name_at_publish: string | null;
  created_at: string;
  question_count: number;
  /** Total attempts against this test. 0 when there are no attempts yet. */
  attempt_count: number;
  // J-X22 availability columns (15-FG/Track-F). All nullable; null/null =
  // always-open. The server's existing `GET /api/tests/:id` route returns
  // these fields when the Test Composer toView mapping is updated; until
  // then they default to null on the client side (TestPayload tolerates the
  // absence at runtime because the cast is a structural one).
  availability_starts_at?: string | null;
  availability_ends_at?: string | null;
  availability_closed_reason?: string | null;
}

export interface TestInputPayload {
  title: string;
  description?: string;
  track: Track;
  level: Level;
  time_limit?: number;
  pass_mark?: number;
  shuffle_q?: boolean;
  shuffle_a?: boolean;
  show_result?: boolean;
  estimated_minutes?: number;
  resit_lockout?: ResitLockoutPolicy;
  issue_certificate?: boolean;
  task_brief_md?: string;
  author_user_id?: string | null;
}

export interface ListPayload {
  items: TestPayload[];
  next_cursor: string | null;
}

export type ApiEnvelope<T> =
  | { ok: true; data: T }
  | { ok: false; error: { code: string; message: string; field?: string } };

async function request<T>(
  path: string,
  init: RequestInit,
): Promise<ApiEnvelope<T>> {
  const res = await fetch(path, { credentials: 'same-origin', ...init });
  const text = await res.text();
  try {
    return JSON.parse(text) as ApiEnvelope<T>;
  } catch {
    return {
      ok: false,
      error: {
        code: 'unexpected_response',
        message:
          'Something went wrong on our side. Please try again, or refresh the page if it keeps happening.',
      },
    };
  }
}

export function listTests(params: {
  track?: Track;
  level?: Level;
  status?: TestStatus;
  cursor?: string;
  limit?: number;
}): Promise<ApiEnvelope<ListPayload>> {
  const usp = new URLSearchParams();
  if (params.track) usp.set('track', params.track);
  if (params.level) usp.set('level', params.level);
  if (params.status) usp.set('status', params.status);
  if (params.cursor) usp.set('cursor', params.cursor);
  if (params.limit !== undefined) usp.set('limit', String(params.limit));
  const qs = usp.toString();
  return request<ListPayload>(`/api/tests${qs ? `?${qs}` : ''}`, {
    method: 'GET',
  });
}

export function getTest(id: string): Promise<ApiEnvelope<TestPayload>> {
  return request<TestPayload>(`/api/tests/${id}`, { method: 'GET' });
}

export function createTest(
  input: TestInputPayload,
): Promise<ApiEnvelope<TestPayload>> {
  return request<TestPayload>('/api/tests', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function updateTest(
  id: string,
  input: Partial<TestInputPayload>,
): Promise<ApiEnvelope<TestPayload>> {
  return request<TestPayload>(`/api/tests/${id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function attachQuestion(
  test_id: string,
  question_id: string,
  sort_order: number,
): Promise<ApiEnvelope<{ test_id: string; question_count: number }>> {
  return request(`/api/tests/${test_id}/questions`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ question_id, sort_order }),
  });
}

export function detachQuestion(
  test_id: string,
  question_id: string,
): Promise<ApiEnvelope<{ test_id: string; question_count: number }>> {
  return request(`/api/tests/${test_id}/questions/${question_id}`, {
    method: 'DELETE',
  });
}

export function reorderQuestions(
  test_id: string,
  ordered_ids: string[],
): Promise<ApiEnvelope<{ test_id: string }>> {
  return request(`/api/tests/${test_id}/questions/reorder`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ordered_ids }),
  });
}

export function setSection(
  test_id: string,
  question_id: string,
  section_name: string | null,
): Promise<ApiEnvelope<{ question_id: string; section_name: string | null }>> {
  return request(`/api/tests/${test_id}/questions/${question_id}/section`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ section_name }),
  });
}

export function publishTest(id: string): Promise<ApiEnvelope<TestPayload>> {
  return request<TestPayload>(`/api/tests/${id}/publish`, { method: 'POST' });
}

export function archiveTest(id: string): Promise<ApiEnvelope<TestPayload>> {
  return request<TestPayload>(`/api/tests/${id}/archive`, { method: 'POST' });
}

// ─────────────────────────────────────────────────────────────────────────────
// Issue 04-test-composer/02 — Task Artefact upload + Task Brief + preview
// ─────────────────────────────────────────────────────────────────────────────

export interface TaskArtefactUploadUrlPayload {
  upload_url: string;
  artefact_key: string;
  expires_at: string;
}

export interface TaskArtefactConfirmPayload {
  task_artifact_url: string;
}

export interface PreviewPayload {
  preview_attempt_id: string;
}

/** Step 1 of 3-step upload: get a presigned PUT URL for the artefact. */
export function getTaskArtefactUploadUrl(
  test_id: string,
  filename: string,
  mime_type: string,
  size_bytes: number,
): Promise<ApiEnvelope<TaskArtefactUploadUrlPayload>> {
  return request<TaskArtefactUploadUrlPayload>(
    `/api/tests/${test_id}/task-artefact-upload-url`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ filename, mime_type, size_bytes }),
    },
  );
}

/** Step 3 of 3-step upload: confirm the upload + attach to the Test. */
export function confirmTaskArtefact(
  test_id: string,
  artefact_key: string,
): Promise<ApiEnvelope<TaskArtefactConfirmPayload>> {
  return request<TaskArtefactConfirmPayload>(
    `/api/tests/${test_id}/task-artefact-confirm`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ artefact_key }),
    },
  );
}

/** Update the Task Brief markdown for a Test. */
export function updateTaskBrief(
  test_id: string,
  task_brief_md: string,
): Promise<ApiEnvelope<TestPayload>> {
  return request<TestPayload>(`/api/tests/${test_id}/task-brief`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ task_brief_md }),
  });
}

/** Create a preview Attempt (status='preview') for the Test. */
export function previewTest(test_id: string): Promise<ApiEnvelope<PreviewPayload>> {
  return request<PreviewPayload>(`/api/tests/${test_id}/preview`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({}),
  });
}
